import React from 'react';
import { Link } from 'react-router-dom';
import { SUPPORT_EMAIL } from '../shared/constants/contactInfo';
import usePageMeta from '../shared/hooks/usePageMeta';

function CookiePolicy() {
  usePageMeta('Cookie Policy', 'How NextAInsight uses cookies and browser storage. We only use essential cookies and Google Analytics.');

  return (
    <div className="container mx-auto px-4 py-16 max-w-4xl selection:bg-slate-blue/30 selection:text-cream">
        <div className="bg-warm-gray/10 backdrop-blur-xl rounded-[2.5rem] shadow-2xl border border-white/5 p-8 md:p-16 relative overflow-hidden">
          <div className="relative z-10">
            <h1 className="text-4xl md:text-5xl font-black text-cream mb-4 tracking-tight">Cookie Policy</h1>
            <p className="text-sm font-bold text-slate-blue uppercase tracking-widest mb-12 border-b border-white/10 pb-6 inline-block">Last Updated: February 2026</p>

            <div className="space-y-10 text-cream/80 leading-relaxed text-lg">

              {/* Introduction */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2"><span className="w-2 h-8 bg-slate-blue rounded-full"></span> About This Policy</h2>
                <p>
                  This Cookie Policy explains how <strong className="text-white">NextAInsight</strong>, operated by hellAIdea Ltd,
                  uses cookies and similar technologies when you visit our website. We believe in transparency
                  about what data is collected and why.
                </p>
              </section>

              {/* What are cookies */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2"><span className="w-2 h-8 bg-slate-blue rounded-full"></span> What Are Cookies?</h2>
                <p>
                  Cookies are small data files stored on your device when you visit a website.
                  Similar technologies include IndexedDB and localStorage, which serve a similar purpose
                  of storing data locally in your browser.
                </p>
              </section>

              {/* What We Use */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2"><span className="w-2 h-8 bg-slate-blue rounded-full"></span> What We Use</h2>
                <p>
                  NextAInsight uses <strong className="text-white">essential cookies and browser storage</strong> required
                  for the service to function, as well as <strong className="text-white">Google Analytics</strong> to collect anonymised usage statistics.
                </p>

                <div className="mt-6 space-y-4">
                  <div className="p-4 bg-white/5 rounded-xl border border-white/5">
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <h3 className="text-white font-bold">Firebase Authentication</h3>
                      <span className="text-xs font-bold text-slate-blue bg-slate-blue/10 px-2 py-1 rounded-full whitespace-nowrap">Essential</span>
                    </div>
                    <p className="text-sm">
                      <strong className="text-white/90">Technology:</strong> IndexedDB (<code className="text-slate-blue/80 text-xs">firebaseLocalStorageDb</code>)
                    </p>
                    <p className="text-sm mt-1">
                      <strong className="text-white/90">Purpose:</strong> Keeps you signed in between visits. Stores your authentication session so you don't need to log in every time you open the app.
                    </p>
                    <p className="text-sm mt-1">
                      <strong className="text-white/90">Duration:</strong> Persistent until you sign out or clear browser data.
                    </p>
                    <p className="text-sm mt-1">
                      <strong className="text-white/90">Set by:</strong> Firebase (Google)
                    </p>
                  </div>

                  <div className="p-4 bg-white/5 rounded-xl border border-white/5">
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <h3 className="text-white font-bold">reCAPTCHA Enterprise</h3>
                      <span className="text-xs font-bold text-slate-blue bg-slate-blue/10 px-2 py-1 rounded-full whitespace-nowrap">Essential</span>
                    </div>
                    <p className="text-sm">
                      <strong className="text-white/90">Technology:</strong> Cookie (<code className="text-slate-blue/80 text-xs">_GRECAPTCHA</code>)
                    </p>
                    <p className="text-sm mt-1">
                      <strong className="text-white/90">Purpose:</strong> Protects the service from bots and automated abuse. Required for Firebase App Check, which verifies that requests to our backend come from the genuine app.
                    </p>
                    <p className="text-sm mt-1">
                      <strong className="text-white/90">Duration:</strong> 6 months.
                    </p>
                    <p className="text-sm mt-1">
                      <strong className="text-white/90">Set by:</strong> Google (<a href="https://policies.google.com/privacy" className="text-slate-blue hover:underline text-xs" target="_blank" rel="noopener noreferrer">Google Privacy Policy</a>)
                    </p>
                  </div>
                  <div className="p-4 bg-white/5 rounded-xl border border-white/5">
                    <div className="flex items-start justify-between gap-4 mb-2">
                      <h3 className="text-white font-bold">Google Analytics</h3>
                      <span className="text-xs font-bold text-amber-400 bg-amber-400/10 px-2 py-1 rounded-full whitespace-nowrap">Analytics</span>
                    </div>
                    <p className="text-sm">
                      <strong className="text-white/90">Technology:</strong> Cookies (<code className="text-slate-blue/80 text-xs">_ga</code>, <code className="text-slate-blue/80 text-xs">_ga_*</code>)
                    </p>
                    <p className="text-sm mt-1">
                      <strong className="text-white/90">Purpose:</strong> Collects anonymised usage statistics such as page views, session duration, and general device/browser information. This helps us understand how the service is used and improve it. No personally identifiable information is collected.
                    </p>
                    <p className="text-sm mt-1">
                      <strong className="text-white/90">Duration:</strong> Up to 2 years.
                    </p>
                    <p className="text-sm mt-1">
                      <strong className="text-white/90">Set by:</strong> Google (<a href="https://policies.google.com/privacy" className="text-slate-blue hover:underline text-xs" target="_blank" rel="noopener noreferrer">Google Privacy Policy</a>)
                    </p>
                    <p className="text-sm mt-1">
                      <strong className="text-white/90">Opt out:</strong> You can opt out of Google Analytics by installing the <a href="https://tools.google.com/dlpage/gaoptout" className="text-slate-blue hover:underline text-xs" target="_blank" rel="noopener noreferrer">Google Analytics Opt-out Browser Add-on</a>.
                    </p>
                  </div>
                </div>
              </section>

              {/* What We Don't Use */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2"><span className="w-2 h-8 bg-slate-blue rounded-full"></span> What We Don't Use</h2>
                <p>NextAInsight does <strong className="text-white">not</strong> use:</p>
                <ul className="list-none mt-4 space-y-3 ml-2">
                  <li className="flex items-start gap-2"><span className="text-red-400 mt-1">&times;</span> Advertising or marketing cookies</li>
                  <li className="flex items-start gap-2"><span className="text-red-400 mt-1">&times;</span> Third-party tracking pixels or beacons</li>
                  <li className="flex items-start gap-2"><span className="text-red-400 mt-1">&times;</span> Social media tracking cookies</li>
                  <li className="flex items-start gap-2"><span className="text-red-400 mt-1">&times;</span> Cross-site advertising or retargeting</li>
                </ul>
              </section>

              {/* Your Choices */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2"><span className="w-2 h-8 bg-slate-blue rounded-full"></span> Your Choices</h2>
                <p>
                  Since we only use essential cookies required for the service to function, there are no optional
                  cookies to accept or reject. You can control cookies through your browser settings:
                </p>
                <ul className="list-none mt-4 space-y-3 ml-2">
                  <li className="flex items-start gap-2"><span className="text-slate-blue mt-1">•</span> <span><strong className="text-white">Blocking cookies:</strong> You can configure your browser to block cookies. However, this will prevent you from signing in to NextAInsight, as authentication requires browser storage.</span></li>
                  <li className="flex items-start gap-2"><span className="text-slate-blue mt-1">•</span> <span><strong className="text-white">Clearing cookies:</strong> You can clear cookies and browser storage at any time. This will sign you out and you will need to log in again.</span></li>
                </ul>
              </section>

              {/* Updates */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2"><span className="w-2 h-8 bg-slate-blue rounded-full"></span> Changes to This Policy</h2>
                <p>
                  If we introduce new cookies or change how we use browser storage, we will update this policy
                  and change the "Last Updated" date. If we ever add non-essential cookies, we will ask for your
                  consent before setting them.
                </p>
              </section>

              {/* Contact */}
              <section>
                <h2 className="text-2xl font-bold text-white mb-4 flex items-center gap-2"><span className="w-2 h-8 bg-slate-blue rounded-full"></span> Contact Us</h2>
                <p>
                  If you have questions about our use of cookies, please email us at{' '}
                  <a href={`mailto:${SUPPORT_EMAIL}`} className="text-slate-blue font-bold hover:text-white transition-colors hover:underline">{SUPPORT_EMAIL}</a>.
                </p>
                <p className="mt-3">
                  For more details about how we handle your personal data, see our{' '}
                  <Link to="/privacy-policy" className="text-slate-blue font-bold hover:text-white transition-colors hover:underline">Privacy Policy</Link>.
                </p>
              </section>
            </div>

          </div>
        </div>
      </div>
  );
}

export default CookiePolicy;
